import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  DataGridPremium as DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { format } from 'date-fns';
import { truncateText } from 'helpers';
import {
  AccessToken_CreateAccessTokenResult,
  AccessToken_FieldsFragment,
  PortalPermissions,
  useAccessToken_DeleteAccessTokenMutation,
  useAccessToken_GetAccessTokensByPortalIdQuery,
  useAccessToken_RevokeAccessTokenMutation,
} from 'queries';
import { MoreVertical } from 'ts-react-feather-icons';

import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import useHasPortalPermission from 'utils/useHasPortalPermission';

import CreateAccessTokenModal from './create-access-token-modal';
import CreateAccessTokenSuccessModal from './create-access-token-success-modal';
import UpdateAccessTokenModal from './update-access-token-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  menuHeader: {
    paddingLeft: 5,
    cursor: 'default',
  },
  description: {
    marginRight: '10px',
  },
  listAction: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default function ManagePortalAccessTokens() {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const [showCreateAccessTokenModal, setShowCreateAccessTokenModal] = useState(false);
  const [showCreateAccessTokenSuccessModal, setShowCreateAccessTokenSuccessModal] = useState(false);
  const [createdAccessTokenResult, setCreatedAccessTokenResult] = useState<
    | (Pick<AccessToken_CreateAccessTokenResult, 'privateAccessKeyText'> & {
        accessToken: AccessToken_FieldsFragment;
      })
    | null
  >(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAccessToken, setSelectedAccessToken] = useState<AccessToken_FieldsFragment | null>(
    null,
  );
  const [showUpdateAccessTokenModal, setShowUpdateAccessTokenModal] = useState(false);

  const hasPortalPermissionAdmin = useHasPortalPermission(PortalPermissions.Admin);

  const {
    data: accessTokensData,
    loading: accessTokensLoading,
    refetch: refetchAccessTokens,
  } = useAccessToken_GetAccessTokensByPortalIdQuery({
    variables: {
      portalId,
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting access tokens by portal ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [revokeAccessToken] = useAccessToken_RevokeAccessTokenMutation({
    onCompleted: () => {
      showToast({
        message: 'The access token has been revoked.',
        severity: NotificationSeverity.SUCCESS,
      });

      refetchAccessTokens();
    },
    onError: (e) => {
      showAlert({
        message: 'Error revoking access token: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const [deleteAccessToken] = useAccessToken_DeleteAccessTokenMutation({
    onCompleted: () => {
      showToast({
        message: 'The access token has been deleted.',
        severity: NotificationSeverity.SUCCESS,
      });

      refetchAccessTokens();
    },
    onError: (e) => {
      showAlert({
        message: 'Error deleting access token: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!accessTokensData || accessTokensLoading) {
    return <Spinner />;
  }

  const handleMenuClick = () => {
    setAnchorEl(null);
    setSelectedAccessToken(null);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleActionClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRevokeAccessToken = async (accessTokenId: string) => {
    if (!window.confirm('Are you sure you want to revoke this access token?')) {
      return;
    }

    await revokeAccessToken({
      variables: {
        input: { id: accessTokenId, portalId },
      },
    });
  };

  const handleDeleteAccessToken = async (accessTokenId: string) => {
    if (!window.confirm('Are you sure you want to delete this access token?')) {
      return;
    }

    await deleteAccessToken({
      variables: {
        input: { id: accessTokenId, portalId },
      },
    });
  };

  const accessTokenColumns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: 'publicAccessToken',
      minWidth: 200,
      headerName: 'Public Token',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return (
          <>
            <List>
              <ListItem>
                <ListItemSecondaryAction className={classes.listAction}>
                  <IconButton
                    id={accessToken.id}
                    edge="end"
                    onClick={(e) => {
                      handleActionClick(e);
                    }}
                    aria-controls={accessToken.id}
                    size="large"
                  >
                    <MoreVertical />
                  </IconButton>
                  <Menu
                    open={anchorEl?.id === accessToken.id}
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={() => handleMenuClick()}
                  >
                    <MenuItem className={classes.menuHeader}>Access Token Actions</MenuItem>
                    {hasPortalPermissionAdmin && !accessToken.isRevoked && (
                      <MenuItem
                        key="edit access token"
                        onClick={() => {
                          handleMenuClick();
                          setSelectedAccessToken(accessToken);
                          setShowUpdateAccessTokenModal(true);
                        }}
                      >
                        Edit Access Token
                      </MenuItem>
                    )}
                    {hasPortalPermissionAdmin && !accessToken.isRevoked && (
                      <MenuItem
                        key="revoke access token"
                        onClick={() => {
                          handleMenuClick();
                          handleRevokeAccessToken(accessToken.id);
                        }}
                      >
                        Revoke Access Token
                      </MenuItem>
                    )}
                    {hasPortalPermissionAdmin &&
                      accessToken.isRevoked &&
                      !accessToken.isDeleted && (
                        <MenuItem
                          key="delete access token"
                          onClick={() => {
                            handleMenuClick();
                            handleDeleteAccessToken(accessToken.id);
                          }}
                        >
                          Delete Access Token
                        </MenuItem>
                      )}
                  </Menu>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            <Tooltip title={accessToken.publicAccessToken}>
              <Typography className={classes.description}>
                {truncateText(accessToken.publicAccessToken, 10)}
              </Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.publicAccessToken;
      },
    },
    {
      disableColumnMenu: true,
      field: 'name',
      minWidth: 150,
      headerName: 'Token Name',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return (
          <>
            <Tooltip title={accessToken.name}>
              <Typography className={classes.description}>{accessToken.name}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.name;
      },
    },
    {
      disableColumnMenu: true,
      field: 'userId',
      minWidth: 200,
      headerName: 'User',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        if (!accessToken.user?.id) {
          return <Typography className={classes.description}>N/A</Typography>;
        }

        const fullName = accessToken.user.firstName + ' ' + accessToken.user.lastName;
        return (
          <>
            <Tooltip title={fullName}>
              <Typography className={classes.description}>{fullName}</Typography>
            </Tooltip>
          </>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        if (!accessToken.user?.id) {
          return 'N/A';
        }

        const fullName = accessToken.user.firstName + ' ' + accessToken.user.lastName;
        return fullName;
      },
    },
    {
      disableColumnMenu: true,
      field: 'lastUsedAt',
      minWidth: 150,
      headerName: 'Last Used Date',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;

        const lastUsedAt = accessToken.lastUsedAt;
        if (!lastUsedAt) {
          return 'N/A';
        }

        const date: string = format(Date.parse(lastUsedAt!.toString()), 'LLL d y');
        const time: string = format(Date.parse(lastUsedAt!.toString()), 'h:mm bbb');

        return (
          <div>
            <div>
              <Typography variant="caption">{date}</Typography>
            </div>
            <div>
              <Typography variant="caption">{time}</Typography>
            </div>
          </div>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.lastUsedAt;
      },
    },
    {
      disableColumnMenu: true,
      field: 'permission',
      minWidth: 200,
      headerName: 'Permission',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return <Typography className={classes.description}>{accessToken.permission}</Typography>;
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.permission;
      },
    },
    {
      disableColumnMenu: true,
      field: 'scope',
      minWidth: 200,
      headerName: 'Scope',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return <Typography className={classes.description}>{accessToken.scope}</Typography>;
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.scope;
      },
    },
    {
      disableColumnMenu: true,
      field: 'isRevoked',
      minWidth: 100,
      headerName: 'Is Revoked',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return (
          <Typography className={classes.description}>
            {accessToken.isRevoked ? 'Yes' : 'No'}
          </Typography>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.isRevoked ? 'Yes' : 'No';
      },
    },
    {
      disableColumnMenu: true,
      field: 'revokedAt',
      minWidth: 150,
      headerName: 'Revoked Date',
      renderCell: (params: GridRenderCellParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;

        const revokedAt = accessToken.revokedAt;
        if (!revokedAt) {
          return 'N/A';
        }

        const date: string = format(Date.parse(revokedAt!.toString()), 'LLL d y');
        const time: string = format(Date.parse(revokedAt!.toString()), 'h:mm bbb');

        return (
          <div>
            <div>
              <Typography variant="caption">{date}</Typography>
            </div>
            <div>
              <Typography variant="caption">{time}</Typography>
            </div>
          </div>
        );
      },
      valueGetter: (params: GridValueGetterParams) => {
        const accessToken = params.row as AccessToken_FieldsFragment;
        return accessToken.revokedAt;
      },
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const accessTokenRows: AccessToken_FieldsFragment[] = [];
  const accessTokens = accessTokensData ? accessTokensData.data : [];
  for (const accessToken of accessTokens) {
    accessTokenRows.push(accessToken);
  }

  return (
    <>
      <CreateAccessTokenModal
        showModal={showCreateAccessTokenModal}
        setShowModal={setShowCreateAccessTokenModal}
        setShowSuccessModal={setShowCreateAccessTokenSuccessModal}
        setCreatedAccessTokenResult={setCreatedAccessTokenResult}
      />
      {createdAccessTokenResult && (
        <CreateAccessTokenSuccessModal
          showModal={showCreateAccessTokenSuccessModal}
          setShowModal={setShowCreateAccessTokenSuccessModal}
          createdAccessTokenResult={createdAccessTokenResult}
        />
      )}
      {selectedAccessToken && (
        <UpdateAccessTokenModal
          accessToken={selectedAccessToken}
          showModal={showUpdateAccessTokenModal}
          setShowModal={setShowUpdateAccessTokenModal}
        />
      )}
      <div style={{ flex: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          style={{ marginTop: '3px', marginBottom: '6px', padding: '2px 10px' }}
          onClick={() => {
            setShowCreateAccessTokenModal(true);
          }}
        >
          + New Access Token
        </Button>
      </div>
      <Grid container spacing={2}>
        <Grid item md={12} xs={12}>
          <div style={{ width: '100%' }}>
            <DataGrid
              className={classes.root}
              columns={accessTokenColumns}
              rows={accessTokenRows}
              disableColumnReorder
              disableRowSelectionOnClick
              autoHeight={true}
              rowHeight={65}
              pagination
              initialState={{
                pagination: { paginationModel: { pageSize: 100 } },
              }}
              components={{
                Toolbar: () => (
                  <Box className={classes.menuHeader}>
                    <Typography variant="h5">Access Tokens</Typography>
                    <hr />
                  </Box>
                ),
              }}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}
